import AuthStore from '@/stores/AuthStore';
import ReservationStore from '@/stores/ReservationStore';
import { IFacility, IPriceDescription } from '@/types';
import { isServer } from '@/utils/isServer';
import { mapCouponError } from '@/utils/tools';
import { formatCurrency } from '@helpers/format';
import { useMobxStores } from '@stores/index';
import { Skeleton } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import GroupDates from './GroupDates';
import { DownOutlined, RightOutlined } from './Icons';

export function displayTotalPrice(
  reservationStore: ReservationStore,
  fromParkingAt?: boolean
): number {
  const processingfeeremove = 0;
  if (reservationStore.bookingPriceDetail.hasDueNow) {
    return (
      reservationStore.bookingPriceDetail.totalPrice +
      reservationStore.bookingPriceDetail.rebookAmount -
      processingfeeremove
    );
  }
  return reservationStore.bookingPriceDetail.rebookRefund
    ? 0
    : reservationStore.bookingPriceDetail.totalPrice - processingfeeremove;
}

function displayDuoNowPrice(
  reservationStore: ReservationStore,
  fromParkingAt?: boolean
): number {
  const processingfeeremove = 0;
  return reservationStore.bookingPriceDetail.rebookRefund
    ? 0
    : reservationStore.bookingPriceDetail.dueNow - processingfeeremove;
}

export interface IBookingDetails {
  title?: string;
  titleStyle?: string;
  showChangeDates?: boolean;
  onDateChange?: () => void;
  detailsFontSize: string;
  facility: IFacility;
  groupedDates?: any;
  checkin?: string;
  checkout?: string;
  facilityImage?: React.ReactElement;
  fromParkingAt?: boolean;
}

function NewLineSmall({
  title,
  detailsFontSize,
  value,
  displayZero,
}: {
  title: string;
  detailsFontSize: any;
  value: number;
  displayZero?: boolean;
}) {
  if (!displayZero && !value) return <></>;
  return (
    <tr className={detailsFontSize}>
      <td>
        <small className="font-bold">{title}</small>
      </td>
      <td className="text-right">
        <small className="font-bold">
          {formatCurrency({
            value: value,
            locale: 'en-US',
            currency: 'USD',
          })}
        </small>
      </td>
    </tr>
  );
}

function NewLine({
  title,
  detailsFontSize,
  value,
}: {
  title: string;
  detailsFontSize: any;
  value: number;
}) {
  if (!value) return <></>;
  return (
    <tr className={detailsFontSize}>
      <td className="pt-2">
        <strong>{title}</strong>
      </td>
      <td className="text-right pt-2">
        <strong>
          {formatCurrency({
            value: value,
            locale: 'en-US',
            currency: 'USD',
          })}
        </strong>
      </td>
    </tr>
  );
}

function InsuranceFee({
  detailsFontSize,
  bookingDetails,
  fromParkingAt,
}: {
  detailsFontSize: any;
  bookingDetails: IPriceDescription;
  fromParkingAt?: boolean;
}) {
  // if (fromParkingAt) return <></>;
  if (!bookingDetails?.insurance_value) return <></>;
  return (
    <>
      <tr className={detailsFontSize}>
        <td className="pt-2">Parking Protection</td>
        <td className="text-right pt-2">
          {formatCurrency({
            value: bookingDetails.insurance_value,
            locale: 'en-US',
            currency: 'USD',
          })}
        </td>
      </tr>
    </>
  );
}

function ProcessingFee({
  detailsFontSize,
  bookingDetails,
  fromParkingAt,
}: {
  detailsFontSize: any;
  bookingDetails: IPriceDescription;
  fromParkingAt?: boolean;
}) {
  // if (fromParkingAt) return <></>;
  if (!bookingDetails?.processing_value) return <></>;
  return (
    <>
      <tr className={detailsFontSize}>
        <td className="pt-2">
          Processing fee ({(100 * bookingDetails?.processing_fee).toFixed(2)}%)
        </td>
        <td className="text-right pt-2">
          {formatCurrency({
            value: bookingDetails.processing_value,
            locale: 'en-US',
            currency: 'USD',
          })}
        </td>
      </tr>
    </>
  );
}

function Rebook({
  detailsFontSize,
  bookingDetails,
  reservationStore,
  fromParkingAt,
}: {
  detailsFontSize: any;
  bookingDetails: IPriceDescription;
  reservationStore: ReservationStore;
  fromParkingAt?: boolean;
}) {
  const processingfeeremove = 0;

  if (!bookingDetails.rebookAmount || bookingDetails.hasDueNow) return <></>;
  const willRefund = bookingDetails.totalPrice < 0;
  return (
    <>
      <NewLine
        title="SubTotal"
        detailsFontSize={detailsFontSize}
        value={
          bookingDetails.totalPrice +
          bookingDetails.rebookAmount -
          processingfeeremove
        }
      />
      <NewLine
        title="Rebook Credit"
        detailsFontSize={detailsFontSize}
        value={bookingDetails.rebookAmount}
      />
      {!!bookingDetails.rebookRefund && (
        <NewLine
          title="Refund"
          detailsFontSize={detailsFontSize}
          value={bookingDetails.rebookRefund + processingfeeremove}
        />
      )}
    </>
  );
}

function DueNowBlock({
  detailsFontSize,
  bookingDetails,
  reservationStore,
  fromParkingAt,
}: {
  detailsFontSize: any;
  bookingDetails: IPriceDescription;
  reservationStore: ReservationStore;
  fromParkingAt?: boolean;
}) {
  if (bookingDetails.rebookAmount) {
    return (
      <>
        <NewLineSmall
          detailsFontSize={detailsFontSize}
          title="Due Now Subtotal"
          value={bookingDetails.dueNowFull}
        />
        <NewLineSmall
          detailsFontSize={detailsFontSize}
          title="Rebook Credit"
          value={bookingDetails.rebookAmount}
        />
        <NewLineSmall
          detailsFontSize={detailsFontSize}
          title="Due Now"
          value={displayDuoNowPrice(reservationStore, fromParkingAt)}
          displayZero={true}
        />
        {!!bookingDetails.differenceOnUs && (
          <NewLineSmall
            detailsFontSize={detailsFontSize}
            title="Difference on Us"
            value={bookingDetails.differenceOnUs}
            displayZero={true}
          />
        )}
        {!!bookingDetails.rebookRefund && (
          <NewLineSmall
            detailsFontSize={detailsFontSize}
            title="Refund"
            value={bookingDetails.rebookRefund}
            displayZero={true}
          />
        )}
        <NewLineSmall
          detailsFontSize={detailsFontSize}
          title="Due At Facility"
          value={bookingDetails.dueAtFacility}
        />
      </>
    );
  }
  return (
    <>
      <NewLineSmall
        detailsFontSize={detailsFontSize}
        title="Due Now"
        value={displayDuoNowPrice(reservationStore, fromParkingAt)}
        displayZero={true}
      />
      <NewLineSmall
        detailsFontSize={detailsFontSize}
        title="Due At Facility"
        value={bookingDetails.dueAtFacility}
      />
    </>
  );
}

function BookingDetails({
  title,
  titleStyle,
  showChangeDates = false,
  onDateChange,
  detailsFontSize,
  facility,
  groupedDates,
  checkin,
  checkout,
  facilityImage,
  fromParkingAt,
}: IBookingDetails) {
  const {
    reservationStore,
    authStore,
  }: { reservationStore: ReservationStore; authStore: AuthStore } =
    useMobxStores();

  const [taxOpened, setTaxOpened] = useState<Boolean>(false);

  useEffect(() => {
    async function loadBooking() {
      if (!checkin || !checkout) return;
      await reservationStore.calculatePrice(
        facility,
        authStore.user?.member_id,
        checkin,
        checkout
      );
      await reservationStore.loadDailyRates(facility, checkin, checkout);
      reservationStore.loadFacilityStatus(
        reservationStore.bookingPriceDetail?.facilitySearch,
        reservationStore.bookingPriceDetail?.validate_warnings
      );
    }
    if (!fromParkingAt) {
      loadBooking();
    }
  }, [
    authStore.user?.member_id,
    checkin,
    checkout,
    facility,
    reservationStore,
  ]);
  const hasSplitTax = reservationStore.bookingPriceDetail?.taxes?.split_taxes;
  const openTax = () => {
    if (hasSplitTax) {
      setTaxOpened(!taxOpened);
    }
  };
  return (
    <Skeleton
      paragraph={{ rows: 4 }}
      title
      loading={reservationStore.loadingPrice}
      className="pb-8"
    >
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-col w-full">
          <h2 className={`${titleStyle} self-start`}>{title}</h2>
          {facilityImage && facilityImage}
        </div>
        {showChangeDates && facility.facility_total_slots !== 0 && (
          <div
            className="text-secondary underline font-medium cursor-pointer"
            onClick={onDateChange}
          >
            Edit
          </div>
        )}
      </div>
      {fromParkingAt ? (
        <> </>
      ) : (
        <div className="grid grid-cols-2 gap-2 w-full mt-4 justify-between">
          <div className="flex flex-col">
            <h2 className="font-bold text-md text-gray-600">Parking Begins</h2>
            <p
              className="text-xs font-bold md:text-[13px] md:font-medium text-gray-600"
              suppressHydrationWarning
            >
              {!isServer && sessionStorage.getItem('checkin')
                ? dayjs(sessionStorage.getItem('checkin')).format(
                    'MMM D, YYYY h:mm A'
                  )
                : dayjs(checkin)
                    .hour(dayjs().hour())
                    .minute(30)
                    .format('MMM D, YYYY h:mm A')}{' '}
            </p>
          </div>
          <div className="flex flex-col">
            <h2 className="font-bold text-md text-gray-600">Parking Ends</h2>
            <p
              className="text-xs font-bold md:text-[13px] md:font-medium text-gray-600"
              suppressHydrationWarning
            >
              {!isServer && sessionStorage.getItem('checkout')
                ? dayjs(sessionStorage.getItem('checkout')).format(
                    'MMM D, YYYY h:mm A'
                  )
                : dayjs(checkout)
                    .hour(dayjs().hour())
                    .minute(30)
                    .format('MMM D, YYYY h:mm A')}{' '}
            </p>
          </div>
        </div>
      )}

      <table width="100%" className="my-4">
        <tbody>
          {groupedDates &&
            groupedDates.length > 0 &&
            !!(
              reservationStore.bookingPriceDetail.facilityParkingType != 3 &&
              reservationStore.bookingPriceDetail.facilityParkingType != 4
            ) && (
              <GroupDates
                groupedDates={groupedDates}
                detailsFontSize={detailsFontSize}
                checkin={checkin}
                checkout={checkout}
              />
            )}
          {(reservationStore.bookingPriceDetail.facilityParkingType == 3 ||
            reservationStore.bookingPriceDetail.facilityParkingType == 4) &&
          reservationStore.bookingPriceDetail.facility_solid_selling_price !=
            0 ? (
            <tr className={detailsFontSize}>
              <td>
                {reservationStore.bookingPriceDetail.facilityParkingType ==
                3 ? (
                  <>
                    1 Room, 1 Night <br /> + Free Parking
                  </>
                ) : (
                  'Monthly Parking'
                )}
              </td>
              <td className="text-right">
                {formatCurrency({
                  value:
                    reservationStore.bookingPriceDetail
                      .facility_solid_selling_price,
                  locale: 'en-US',
                  currency: 'USD',
                })}
              </td>
            </tr>
          ) : (
            (!groupedDates || groupedDates.length == 0) && (
              <>
                <tr className={detailsFontSize}>
                  <td>BasePrice</td>
                  <td className="text-right">
                    {formatCurrency({
                      value: reservationStore.bookingPriceDetail.basePrice,
                      locale: 'en-US',
                      currency: 'USD',
                    })}
                  </td>
                </tr>
              </>
            )
          )}

          {reservationStore.bookingPriceDetail.fullNosonFee +
            reservationStore.bookingPriceDetail.discount_last_day ===
          0 ? (
            <></>
          ) : (
            <tr className={detailsFontSize}>
              <td>{facility.label_facility_fee || 'Fee'}</td>
              <td className="text-right">
                {formatCurrency({
                  value:
                    reservationStore.bookingPriceDetail.fullNosonFee +
                    reservationStore.bookingPriceDetail.discount_last_day,
                  locale: 'en-US',
                  currency: 'USD',
                })}
              </td>
            </tr>
          )}
          {reservationStore.bookingPriceDetail.airportFee === 0 ? (
            <></>
          ) : (
            <tr className={detailsFontSize}>
              <td>{facility.label_facility_afee || 'Airport Access Fee'}</td>
              <td className="text-right">
                {formatCurrency({
                  value: reservationStore.bookingPriceDetail.airportFee,
                  locale: 'en-US',
                  currency: 'USD',
                })}
              </td>
            </tr>
          )}
          {reservationStore.bookingPriceDetail.tax === 0 ? (
            <></>
          ) : (
            <tr className={detailsFontSize}>
              <td className="">
                {' '}
                {hasSplitTax ? (
                  <a onClick={openTax}>
                    {taxOpened ? <DownOutlined /> : <></>}{' '}
                    {!taxOpened ? <RightOutlined /> : <></>} Tax
                  </a>
                ) : (
                  <>Tax</>
                )}
              </td>
              <td className="text-right">
                {formatCurrency({
                  value: reservationStore.bookingPriceDetail.tax,
                  locale: 'en-US',
                  currency: 'USD',
                })}
              </td>
            </tr>
          )}
          {taxOpened &&
          reservationStore.bookingPriceDetail.taxes?.value_extra_tax_fee ? (
            <tr className={detailsFontSize}>
              <td className="pb-2">
                {facility.label_extra_tax_fee || 'Extra Tax Fee'}
              </td>
              <td className="text-right pb-2">
                {formatCurrency({
                  value:
                    reservationStore.bookingPriceDetail.taxes
                      ?.value_extra_tax_fee,
                  locale: 'en-US',
                  currency: 'USD',
                })}
              </td>
            </tr>
          ) : (
            <></>
          )}
          {taxOpened &&
          reservationStore.bookingPriceDetail.taxes
            ?.value_facility_static_tax ? (
            <tr className={detailsFontSize}>
              <td className="pb-2">
                {facility.label_facility_static_tax || 'Static Tax Fee'}
              </td>
              <td className="text-right pb-2">
                {formatCurrency({
                  value:
                    reservationStore.bookingPriceDetail.taxes
                      ?.value_facility_static_tax,
                  locale: 'en-US',
                  currency: 'USD',
                })}
              </td>
            </tr>
          ) : (
            <></>
          )}
          {taxOpened &&
          reservationStore.bookingPriceDetail.taxes?.value_facility_tax ? (
            <tr className={detailsFontSize}>
              <td className="pb-2">
                {facility.label_facility_tax || 'Facility Tax Fee'}
              </td>
              <td className="text-right pb-2">
                {formatCurrency({
                  value:
                    reservationStore.bookingPriceDetail.taxes
                      ?.value_facility_tax,
                  locale: 'en-US',
                  currency: 'USD',
                })}
              </td>
            </tr>
          ) : (
            <></>
          )}
          {taxOpened &&
          reservationStore.bookingPriceDetail.taxes?.value_solid_tax_fee ? (
            <tr className={detailsFontSize}>
              <td className="pb-2">
                {facility.label_solid_tax_fee || 'Extra Tax Fee'}
              </td>
              <td className="text-right pb-2">
                {formatCurrency({
                  value:
                    reservationStore.bookingPriceDetail.taxes
                      ?.value_solid_tax_fee,
                  locale: 'en-US',
                  currency: 'USD',
                })}
              </td>
            </tr>
          ) : (
            <></>
          )}

          {reservationStore.error_coupon ? (
            <tr className={detailsFontSize}>
              <td className="pb-2">Discount**</td>
              <td className="text-right pb-2">
                {formatCurrency({
                  value:
                    reservationStore.bookingPriceDetail.fullNosonFee +
                    reservationStore.bookingPriceDetail.discount_last_day -
                    reservationStore.bookingPriceDetail.discountedNosonFee,
                  locale: 'en-US',
                  currency: 'USD',
                })}
              </td>
            </tr>
          ) : (
            <></>
          )}
          <InsuranceFee
            detailsFontSize={detailsFontSize}
            bookingDetails={reservationStore.bookingPriceDetail}
            fromParkingAt={fromParkingAt}
          />
          <ProcessingFee
            detailsFontSize={detailsFontSize}
            bookingDetails={reservationStore.bookingPriceDetail}
            fromParkingAt={fromParkingAt}
          />
          {reservationStore.bookingPriceDetail.hasDiscount ? (
            <>
              <tr className={detailsFontSize}>
                <td className="pb-2">
                  <span>Discount**</span>
                </td>
                <td className="text-right pb-2">
                  -
                  {formatCurrency({
                    value:
                      reservationStore.bookingPriceDetail.fullNosonFee +
                      reservationStore.bookingPriceDetail.discount_last_day -
                      reservationStore.bookingPriceDetail.discountedNosonFee,
                    locale: 'en-US',
                    currency: 'USD',
                  })}
                </td>
              </tr>
              <Rebook
                detailsFontSize={detailsFontSize}
                bookingDetails={reservationStore.bookingPriceDetail}
                reservationStore={reservationStore}
                fromParkingAt={fromParkingAt}
              />
              <tr className="border-b border-black border-opacity-25 text-base">
                <td></td>
              </tr>
              <tr className={detailsFontSize}>
                <td className="pt-2">
                  <strong>TOTAL</strong>
                </td>
                <td className="text-right pt-2">
                  <strong>
                    {formatCurrency({
                      value: displayTotalPrice(reservationStore, fromParkingAt),
                      locale: 'en-US',
                      currency: 'USD',
                    })}
                  </strong>
                </td>
              </tr>
            </>
          ) : (
            <>
              <tr className="border-b border-black border-opacity-25 text-base">
                <td></td>
              </tr>{' '}
              <Rebook
                detailsFontSize={detailsFontSize}
                bookingDetails={reservationStore.bookingPriceDetail}
                reservationStore={reservationStore}
                fromParkingAt={fromParkingAt}
              />
              <tr className={detailsFontSize}>
                <td className="pt-2">
                  <strong>TOTAL</strong>
                </td>
                <td className="text-right pt-2">
                  <strong>
                    {formatCurrency({
                      value: displayTotalPrice(reservationStore, fromParkingAt),
                      locale: 'en-US',
                      currency: 'USD',
                    })}
                  </strong>
                </td>
              </tr>
            </>
          )}
          {reservationStore.bookingPriceDetail.hasDueNow && (
            <DueNowBlock
              bookingDetails={reservationStore.bookingPriceDetail}
              detailsFontSize={detailsFontSize}
              reservationStore={reservationStore}
              fromParkingAt={fromParkingAt}
            />
          )}
        </tbody>
      </table>
      {facility.facility_24h_extension == 5 ? (
        <p className="my-2 relative block">
          <span className="text-gray-500">* Special prices for 12h period</span>
        </p>
      ) : (
        <p className="my-2 relative block">
          <span className="text-gray-500">* Price is based on 24h period</span>
        </p>
      )}
      {reservationStore.error_coupon ? (
        <p className="my-2 relative block">
          <span className="text-red-500 font-bold">
            ** {mapCouponError(reservationStore.error_coupon)}
          </span>
        </p>
      ) : (
        <></>
      )}
      {reservationStore.coupon_applied ? (
        <p className="my-2 relative block text-green-500">
          ** Discount applied{' '}
          <small className="text-sm text-gray-600">
            ({reservationStore.coupon_applied})
          </small>
        </p>
      ) : (
        <></>
      )}
    </Skeleton>
  );
}

export default observer(BookingDetails);
